import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Account, CreateTransactionRequest, JournalEntryInput } from '../../../services/ledger-client/types';

type TransactionPattern = 'journal' | 'split-credit' | 'split-debit';
type EntrySide = 'debit' | 'credit';

interface EntryRowValue {
  accountId: string;
  side: EntrySide;
  amount: number;
  memo?: string;
}

export interface LedgerTransactionDialogData {
  accounts: Account[];
}

@Component({
    selector: 'app-ledger-transaction-dialog',
    templateUrl: './ledger-transaction-dialog.component.html',
    styleUrls: ['./ledger-transaction-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LedgerTransactionDialogComponent implements OnInit {
  form: UntypedFormGroup = this.formBuilder.group({
    pattern: ['journal', Validators.required],
    source: ['finance-admin'],
    description: [''],
    externalReference: [''],
    postedAt: [''],
    anchorAccountId: [''],
    rows: this.formBuilder.array([]),
  });

  errorMessage = '';

  readonly patternOptions: Array<{ value: TransactionPattern; label: string; hint: string }> = [
    {
      value: 'journal',
      label: 'General Journal',
      hint: 'Manual debit/credit lines. Must stay balanced.',
    },
    {
      value: 'split-credit',
      label: 'Split Credit',
      hint: 'One debit account split to many credit accounts.',
    },
    {
      value: 'split-debit',
      label: 'Split Debit',
      hint: 'One credit account split to many debit accounts.',
    },
  ];

  readonly sortedAccounts: Account[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LedgerTransactionDialogData,
    private dialogRef: MatDialogRef<LedgerTransactionDialogComponent, CreateTransactionRequest | null>,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.sortedAccounts = [...(data?.accounts || [])].sort((a, b) => {
      const aValue = `${a.code || ''} ${a.name || a.id}`.toLowerCase();
      const bValue = `${b.code || ''} ${b.name || b.id}`.toLowerCase();
      if (aValue > bValue) {
        return 1;
      }
      if (aValue < bValue) {
        return -1;
      }
      return 0;
    });
  }

  ngOnInit(): void {
    const today = new Date().toISOString().slice(0, 10);
    this.form.get('postedAt')?.setValue(today);
    this.applyPatternDefaults(this.currentPattern);

    this.form.get('pattern')?.valueChanges.subscribe((pattern: TransactionPattern) => {
      this.errorMessage = '';
      this.applyPatternDefaults(pattern);
    });
  }

  get rows(): UntypedFormArray {
    return this.form.get('rows') as UntypedFormArray;
  }

  get rowGroups(): UntypedFormGroup[] {
    return this.rows.controls as UntypedFormGroup[];
  }

  get currentPattern(): TransactionPattern {
    return this.form.get('pattern')?.value as TransactionPattern;
  }

  get isJournalPattern(): boolean {
    return this.currentPattern === 'journal';
  }

  get currentPatternHint(): string {
    const selected = this.patternOptions.find((option) => option.value === this.currentPattern);
    return selected?.hint || '';
  }

  get anchorSide(): EntrySide {
    return this.currentPattern === 'split-credit' ? 'debit' : 'credit';
  }

  get splitSide(): EntrySide {
    return this.anchorSide === 'debit' ? 'credit' : 'debit';
  }

  get canSubmit(): boolean {
    return this.sortedAccounts.length > 0;
  }

  addRow(): void {
    this.rows.push(this.createRowGroup(this.isJournalPattern ? 'debit' : this.splitSide));
  }

  removeRow(index: number): void {
    if (this.rows.length <= this.minimumRowCount()) {
      return;
    }
    this.rows.removeAt(index);
  }

  save(): void {
    this.errorMessage = '';
    this.form.markAllAsTouched();

    if (!this.canSubmit) {
      this.errorMessage = 'No accounts are available yet.';
      return;
    }

    if (this.form.invalid) {
      this.errorMessage = 'Please complete required fields.';
      return;
    }

    const rows = this.parseRows();
    if (!rows.length) {
      this.errorMessage = 'Add at least one entry row.';
      return;
    }

    const entries = this.buildEntries(rows);
    if (!entries) {
      return;
    }

    const postedAtValue = String(this.form.get('postedAt')?.value || '').trim();
    const sourceValue = String(this.form.get('source')?.value || '').trim();
    const descriptionValue = String(this.form.get('description')?.value || '').trim();
    const externalReferenceValue = String(this.form.get('externalReference')?.value || '').trim();

    const payload: CreateTransactionRequest = {
      source: sourceValue || undefined,
      description: descriptionValue || undefined,
      externalReference: externalReferenceValue || undefined,
      postedAt: postedAtValue ? new Date(postedAtValue).toISOString() : undefined,
      entries,
    };

    this.dialogRef.close(payload);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  accountLabel(account: Account): string {
    const code = account.code ? `${account.code} - ` : '';
    return `${code}${account.name} (${account.id})`;
  }

  private applyPatternDefaults(pattern: TransactionPattern): void {
    const anchorControl = this.form.get('anchorAccountId');

    while (this.rows.length) {
      this.rows.removeAt(0);
    }

    if (pattern === 'journal') {
      anchorControl?.clearValidators();
      this.rows.push(this.createRowGroup('debit'));
      this.rows.push(this.createRowGroup('credit'));
      anchorControl?.setValue('');
      anchorControl?.updateValueAndValidity();
      return;
    }

    anchorControl?.setValidators([Validators.required]);
    anchorControl?.updateValueAndValidity();
    this.rows.push(this.createRowGroup(this.splitSide));
  }

  private createRowGroup(side: EntrySide): UntypedFormGroup {
    return this.formBuilder.group({
      accountId: ['', Validators.required],
      side: [side, Validators.required],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      memo: [''],
    });
  }

  private minimumRowCount(): number {
    return this.currentPattern === 'journal' ? 2 : 1;
  }

  private parseRows(): EntryRowValue[] {
    return this.rowGroups
      .map((row) => {
        const amount = Number(row.get('amount')?.value);
        return {
          accountId: String(row.get('accountId')?.value || '').trim(),
          side: row.get('side')?.value as EntrySide,
          amount: this.roundAmount(amount),
          memo: String(row.get('memo')?.value || '').trim() || undefined,
        };
      })
      .filter((row) => row.accountId && Number.isFinite(row.amount) && row.amount > 0);
  }

  private buildEntries(rows: EntryRowValue[]): JournalEntryInput[] | null {
    if (this.currentPattern === 'journal') {
      if (rows.length < 2) {
        this.errorMessage = 'Journal entries require at least two lines.';
        return null;
      }

      const debitTotal = this.roundAmount(rows
        .filter(row => row.side === 'debit')
        .reduce((sum, row) => sum + row.amount, 0));
      const creditTotal = this.roundAmount(rows
        .filter(row => row.side === 'credit')
        .reduce((sum, row) => sum + row.amount, 0));

      if (!this.amountsEqual(debitTotal, creditTotal)) {
        this.errorMessage = `Debits (${debitTotal.toFixed(2)}) must equal credits (${creditTotal.toFixed(2)}).`;
        return null;
      }

      return rows.map((row) => this.toJournalEntry(row));
    }

    const anchorAccountId = String(this.form.get('anchorAccountId')?.value || '').trim();
    if (!anchorAccountId) {
      this.errorMessage = 'Select the main account for this split pattern.';
      return null;
    }

    const splitTotal = this.roundAmount(rows.reduce((sum, row) => sum + row.amount, 0));
    if (splitTotal <= 0) {
      this.errorMessage = 'Split total must be greater than zero.';
      return null;
    }

    const entries: JournalEntryInput[] = [
      this.anchorSide === 'debit'
        ? { accountId: anchorAccountId, debitAmount: splitTotal }
        : { accountId: anchorAccountId, creditAmount: splitTotal },
      ...rows.map((row) => ({
        accountId: row.accountId,
        debitAmount: this.splitSide === 'debit' ? row.amount : undefined,
        creditAmount: this.splitSide === 'credit' ? row.amount : undefined,
        memo: row.memo,
      })),
    ];

    return entries;
  }

  private toJournalEntry(row: EntryRowValue): JournalEntryInput {
    if (row.side === 'debit') {
      return {
        accountId: row.accountId,
        debitAmount: row.amount,
        memo: row.memo,
      };
    }

    return {
      accountId: row.accountId,
      creditAmount: row.amount,
      memo: row.memo,
    };
  }

  private roundAmount(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private amountsEqual(value1: number, value2: number): boolean {
    return Math.abs(value1 - value2) < 0.005;
  }
}
